/**
 * StructuredForm — formulário com campos pré-definidos.
 *
 * Slide do tipo mission com missionId='lab.form.structured'. Em PR-2 só
 * modo solo (scope='student'). Modo equipe (scope='team' + fieldsByAssignment)
 * entra no PR-3 com a infra de assignments.
 *
 * Schema do frontmatter — ver SPEC-ATLAS-V2 §2.1.
 */

import { useState } from 'react'

import { useAdapter } from '../../adapter'
import type { MissionComponentProps } from '../../games/atlas/components'

interface FormField {
  id: string
  label: string
  type: 'textarea' | 'text' | 'select'
  required?: boolean
  placeholder?: string
  maxLength?: number
  options?: string[] // só para type='select'
}

export function StructuredForm(props: MissionComponentProps) {
  const adapter = useAdapter()
  const config = props.slideConfig ?? {}
  const slideIndex = props.slideIndex ?? 0
  const scope = (config.scope as string) || 'student'
  // PR-3: em modo team, resolve fields a partir de myAssignment.payload.fields
  // (entregue pelo backend via assignmentReceived). Em modo solo, usa
  // config.fields direto. Falta de myAssignment em modo team rende guidance
  // pro mestre atribuir as equipes.
  const myAssignmentFields = props.myAssignment?.fields as FormField[] | undefined
  const fields: FormField[] = scope === 'team'
    ? (Array.isArray(myAssignmentFields) ? myAssignmentFields : [])
    : (Array.isArray(config.fields) ? (config.fields as FormField[]) : [])

  const isTeamMode = scope === 'team'
  const hasAssignment = isTeamMode && Array.isArray(myAssignmentFields)

  const [values, setValues] = useState<Record<string, string>>({})
  const [submitted, setSubmitted] = useState(false)

  function updateField(id: string, value: string) {
    setValues((v) => ({ ...v, [id]: value }))
  }

  function handleSubmit() {
    if (props.readOnly) return
    // Valida required
    const missing = fields.filter((f) => f.required && !values[f.id]?.trim())
    if (missing.length > 0) return
    adapter.submitForm({ slideIndex, values })
    setSubmitted(true)
    if (props.onMissionEvent) {
      props.onMissionEvent('submitted', { fieldIds: Object.keys(values) })
    }
  }

  if (fields.length === 0) {
    if (isTeamMode && !hasAssignment) {
      return (
        <div
          style={{
            padding: 24,
            maxWidth: 600,
            margin: '0 auto',
            background: 'var(--color-lab-bg-2, #F2F2ED)',
            borderRadius: 8,
            textAlign: 'center',
          }}
        >
          <p style={{ margin: '0 0 8px', fontWeight: 500 }}>Aguardando equipe…</p>
          <p style={{ margin: 0, opacity: 0.7, fontSize: '0.9em' }}>
            O mestre ainda não atribuiu as equipes para este slide. Quando ele
            atribuir, os campos da sua equipe aparecem aqui.
          </p>
        </div>
      )
    }
    return (
      <div style={{ padding: 24, color: 'var(--color-lab-rose, #D4474A)' }}>
        Form sem campos configurados.
      </div>
    )
  }

  return (
    <div
      style={{
        maxWidth: 800,
        margin: '0 auto',
        padding: 'var(--spacing-lab-5, 24px)',
        display: 'flex',
        flexDirection: 'column',
        gap: 'var(--spacing-lab-4, 16px)',
      }}
    >
      {isTeamMode && props.myTeam && (
        <div
          style={{
            padding: 8,
            background: 'var(--color-lab-bg-2, #F2F2ED)',
            borderLeft: '3px solid var(--color-lab-accent, #5B2DB8)',
            fontSize: '0.85em',
          }}
        >
          <strong>Equipe:</strong> {props.myTeam}
        </div>
      )}
      {fields.map((f) => {
        const v = values[f.id] ?? ''
        const max = f.maxLength ?? 1000
        return (
          <div key={f.id} style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            <label style={{ fontWeight: 500 }}>
              {f.label}
              {f.required && <span style={{ color: 'var(--color-lab-rose, #D4474A)' }}> *</span>}
            </label>
            {f.type === 'textarea' && (
              <textarea
                value={v}
                onChange={(e) => updateField(f.id, e.target.value.slice(0, max))}
                placeholder={f.placeholder}
                disabled={props.readOnly}
                rows={4}
                style={{
                  width: '100%',
                  padding: 10,
                  fontFamily: 'inherit',
                  fontSize: 'var(--text-lab-base, 1rem)',
                  border: '1px solid var(--color-lab-rule, #D8D5CB)',
                  borderRadius: 6,
                  resize: 'vertical',
                }}
              />
            )}
            {f.type === 'text' && (
              <input
                type="text"
                value={v}
                onChange={(e) => updateField(f.id, e.target.value.slice(0, max))}
                placeholder={f.placeholder}
                disabled={props.readOnly}
                style={{
                  width: '100%',
                  padding: 10,
                  fontSize: 'var(--text-lab-base, 1rem)',
                  border: '1px solid var(--color-lab-rule, #D8D5CB)',
                  borderRadius: 6,
                }}
              />
            )}
            {f.type === 'select' && (
              <select
                value={v}
                onChange={(e) => updateField(f.id, e.target.value)}
                disabled={props.readOnly}
                style={{
                  width: '100%',
                  padding: 10,
                  fontSize: 'var(--text-lab-base, 1rem)',
                  border: '1px solid var(--color-lab-rule, #D8D5CB)',
                  borderRadius: 6,
                }}
              >
                <option value="">— selecione —</option>
                {(f.options ?? []).map((opt) => (
                  <option key={opt} value={opt}>
                    {opt}
                  </option>
                ))}
              </select>
            )}
            {f.type === 'textarea' && (
              <span style={{ alignSelf: 'flex-end', opacity: 0.6, fontSize: '0.85em' }}>
                {v.length} / {max}
              </span>
            )}
          </div>
        )
      })}

      <button
        onClick={handleSubmit}
        disabled={props.readOnly}
        style={{
          padding: '10px 28px',
          background: submitted
            ? 'var(--color-lab-accent, #6aa)'
            : 'var(--color-lab-accent, #5B2DB8)',
          color: 'white',
          border: 'none',
          borderRadius: 6,
          cursor: 'pointer',
          fontSize: 'var(--text-lab-base, 1rem)',
          alignSelf: 'flex-end',
        }}
      >
        {submitted ? 'Atualizar' : 'Enviar'}
      </button>

      {submitted && (
        <div style={{ opacity: 0.7, fontSize: '0.9em' }}>
          Resposta registrada. Você pode editar e reenviar.
        </div>
      )}
    </div>
  )
}
